/**
 * useRecentItems hook
 *
 * React hook for reading recent items with pin/unpin functionality.
 * Provides reactive access to recent items stored in localStorage.
 */

import { RECENT_ITEMS_STORAGE_KEY, RecentItemEntry } from 'lib/constants/recent-items'
import {
    getRecentItems as getRecentItemsFromStore,
    isPinned as isPinnedFromStore,
    pinItem as pinItemInStore,
    unpinItem as unpinItemInStore,
} from 'lib/utils/recent-items-tracker'
import { useCallback, useEffect, useState } from 'react'

export type UseRecentItemsOptions = {
    maxItems?: number
}

export type UseRecentItemsResult = {
    items: RecentItemEntry[]
    isLoading: boolean
    pinItem: (id: string) => void
    unpinItem: (id: string) => void
    isPinned: (id: string) => boolean
    refresh: () => void
}

/**
 * Hook for reading recent items with pin/unpin functionality
 *
 * @param category - The category to get items for (e.g., 'apps', 'groups')
 * @param options.maxItems - Maximum number of items to return (pinned items count toward this limit)
 *
 * @returns Object with items, loading state, pin/unpin functions, and refresh function
 */
export function useRecentItems(
    category: string,
    options: UseRecentItemsOptions = {},
): UseRecentItemsResult {
    const { maxItems } = options
    const [items, setItems] = useState<RecentItemEntry[]>([])
    const [isLoading, setIsLoading] = useState(true)

    // Load items from localStorage
    const loadItems = useCallback(() => {
        setItems(getRecentItemsFromStore(category, { maxItems }))
        setIsLoading(false)
    }, [category, maxItems])

    // Initial load
    useEffect(() => {
        loadItems()
    }, [loadItems])

    // Listen for localStorage changes from other tabs/windows
    useEffect(() => {
        const handleStorageChange = (event: StorageEvent): void => {
            if (event.key === RECENT_ITEMS_STORAGE_KEY) {
                loadItems()
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => {
            window.removeEventListener('storage', handleStorageChange)
        }
    }, [loadItems])

    // Pin an item and refresh the list
    const pinItem = useCallback((id: string) => {
        pinItemInStore(category, id)
        loadItems()
    }, [category, loadItems])

    // Unpin an item and refresh the list
    const unpinItem = useCallback((id: string) => {
        unpinItemInStore(category, id)
        loadItems()
    }, [category, loadItems])

    // Check if an item is pinned
    const isPinned = useCallback((id: string) => {
        return isPinnedFromStore(category, id)
    }, [category])

    // Manual refresh function
    const refresh = useCallback(() => {
        loadItems()
    }, [loadItems])

    return {
        items,
        isLoading,
        pinItem,
        unpinItem,
        isPinned,
        refresh,
    }
}
